/**
 * Shared API client for making authenticated HTTP requests with timeouts.
 *
 * All direct axios/fetch calls throughout the app should use this module
 * instead of calling axios or fetch directly. This ensures consistent
 * timeouts, auth headers, and error handling.
 *
 * Authentication uses httpOnly cookies set by the API. The browser sends
 * the session cookie automatically with `withCredentials: true`. No token
 * is read from localStorage or injected into the Authorization header.
 */

import axios, { AxiosError, AxiosResponse } from "axios";

import { orgSessionManager } from "@domains/auth/services/orgSessionManager";
import { Timeouts } from "@platform/constants/Common";
import { apiBaseUrls } from "@platform/constants/shared-constants";
import { buildLoginPath } from "@platform/utils/authNavigation";

// ── Types ────────────────────────────────────────────────────────────────────

export interface ApiErrorResponse {
  detail?: string; // RFC 7807 Problem Details (primary field from AllExceptionsFilter)
  error?: string;
  message?: string;
  statusCode?: number;
  validationErrors?: { path: string; message: string }[];
  errors?: { path: string; message: string }[];
  sagaId?: string;
}

export class ApiRequestError extends Error {
  status: number;
  data?: ApiErrorResponse;

  constructor(message: string, status: number, data?: ApiErrorResponse) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.data = data;
  }
}

// ── Default config ─────────────────────────────────────────────────────────────

const DEFAULT_TIMEOUT = Timeouts.API_REQUEST; // 30s
const LONG_TIMEOUT = 60_000; // 60s for build/deploy operations

// ── Shared axios instance ─────────────────────────────────────────────────────

const apiClient = axios.create({
  timeout: DEFAULT_TIMEOUT,
  withCredentials: true, // Send httpOnly session cookie
});

// Request interceptor: wait for any in-progress org switch before sending requests.
apiClient.interceptors.request.use(async (config) => {
  if (orgSessionManager.isSwitching()) {
    await orgSessionManager.waitForSwitch();
  }
  return config;
});

// Response interceptor for 401 handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      const request = error.config as
        (typeof error.config & { authRefreshRetry?: boolean }) | undefined;
      const isRefreshRequest = request?.url?.endsWith("/api/v1/auth/refresh");
      if (!isRefreshRequest && !request?.authRefreshRetry) {
        try {
          await apiClient.post("/api/v1/auth/refresh");
          if (request) {
            request.authRefreshRetry = true;
            return apiClient.request(request);
          }
        } catch {
          // Fall through to the normal login redirect below.
        }
      }
      // Clear in-memory auth state and redirect to login
      orgSessionManager.clearSession();
      window.location.href = buildLoginPath(
        `${window.location.pathname}${window.location.search}${window.location.hash}`,
      );
    }
    return Promise.reject(error);
  },
);

// ── Exported helpers ──────────────────────────────────────────────────────────

/**
 * Make an authenticated GET request with timeout.
 *
 * @param path - Full URL or path (if starts with /, prepended with apiBaseUrls.BACKEND)
 * @param options - Optional axios config overrides (e.g. custom timeout)
 */
export async function apiGet<T>(
  path: string,
  options?: { timeout?: number; headers?: Record<string, string> },
): Promise<T> {
  const url = resolveUrl(path);
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT;
  try {
    const response: AxiosResponse<T> = await apiClient.get<T>(url, {
      timeout,
      headers: options?.headers,
    });
    return response.data;
  } catch (err) {
    throw normalizeError(err);
  }
}

/**
 * Make an authenticated POST request with timeout.
 */
export async function apiPost<T, D = unknown>(
  path: string,
  data?: D,
  options?: { timeout?: number; headers?: Record<string, string> },
): Promise<T> {
  const url = resolveUrl(path);
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT;
  try {
    const response: AxiosResponse<T> = await apiClient.post<T>(url, data, {
      timeout,
      headers: options?.headers,
    });
    return response.data;
  } catch (err) {
    throw normalizeError(err);
  }
}

/**
 * Make an authenticated PATCH request with timeout.
 */
export async function apiPatch<T, D = unknown>(
  path: string,
  data?: D,
  options?: { timeout?: number; headers?: Record<string, string> },
): Promise<T> {
  const url = resolveUrl(path);
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT;
  try {
    const response: AxiosResponse<T> = await apiClient.patch<T>(url, data, {
      timeout,
      headers: options?.headers,
    });
    return response.data;
  } catch (err) {
    throw normalizeError(err);
  }
}

/**
 * Make an authenticated PUT request with timeout.
 */
export async function apiPut<T, D = unknown>(
  path: string,
  data?: D,
  options?: { timeout?: number; headers?: Record<string, string> },
): Promise<T> {
  const url = resolveUrl(path);
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT;
  try {
    const response: AxiosResponse<T> = await apiClient.put<T>(url, data, {
      timeout,
      headers: options?.headers,
    });
    return response.data;
  } catch (err) {
    throw normalizeError(err);
  }
}

/**
 * Make an authenticated DELETE request with timeout.
 */
export async function apiDelete<T>(
  path: string,
  options?: { timeout?: number; headers?: Record<string, string> },
): Promise<T> {
  const url = resolveUrl(path);
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT;
  try {
    const response: AxiosResponse<T> = await apiClient.delete<T>(url, {
      timeout,
      headers: options?.headers,
    });
    return response.data;
  } catch (err) {
    throw normalizeError(err);
  }
}

/**
 * Make an authenticated fetch() request with AbortController timeout.
 * For use in components that prefer the fetch API over axios.
 */
export async function apiFetch<T>(
  path: string,
  options?: {
    method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
    body?: unknown;
    timeout?: number;
    headers?: Record<string, string>;
  },
): Promise<T> {
  const url = resolveUrl(path);
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      method: options?.method ?? "GET",
      signal: controller.signal,
      credentials: "include", // Send httpOnly session cookie
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
      ...(options?.body !== undefined
        ? { body: JSON.stringify(options.body) }
        : {}),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new ApiRequestError(
        body?.detail ??
          body?.message ??
          body?.error ??
          `Request failed (${response.status})`,
        response.status,
        body,
      );
    }

    return (await response.json()) as T;
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ApiRequestError(
        `Request timed out after ${timeout / 1000}s`,
        408,
      );
    }
    if (err instanceof ApiRequestError) throw err;
    throw normalizeError(err);
  } finally {
    clearTimeout(timeoutId);
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function resolveUrl(path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${apiBaseUrls.BACKEND}${path}`;
}

function normalizeError(err: unknown): ApiRequestError {
  if (err instanceof ApiRequestError) return err;

  if (axios.isAxiosError(err)) {
    const status = err.response?.status ?? 0;
    const data = err.response?.data as ApiErrorResponse | undefined;
    const message =
      data?.detail ??
      data?.message ??
      data?.error ??
      err.message ??
      "Request failed";
    return new ApiRequestError(message, status, data);
  }

  if (err instanceof Error) {
    if (err.name === "AbortError") {
      return new ApiRequestError("Request timed out", 408);
    }
    return new ApiRequestError(err.message, 0);
  }

  return new ApiRequestError("An unknown error occurred", 0);
}

// Export timeouts for use in components
export { DEFAULT_TIMEOUT, LONG_TIMEOUT };
